import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoggerService } from '@/common/infrastructure/services/LoggerService';
import { organizationTimezoneService } from '@/common/infrastructure/services/OrganizationTimezoneService';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead/index';
import { useTimeZoneTypeaheadFetch } from '@/jobs/application/adapters/listTimeZonesAutocomplete';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import timezoneMapping from './timezoneMapping.json';

// Define the interface locally since TimezoneSelector was deleted
export interface TimezoneHierarchyContext {
  jobTimezone?: string | null;
  jobsiteCoordinates?: {
    latitude: number;
    longitude: number;
  } | null;
  userOverride?: string | null;
}

// Create a logger instance for this hook
const logger = LoggerService.create('useJobTimezoneHierarchy');

// Define the interface for timezone data
interface TimezoneData {
  timeZone: string;
}

// Type alias for timezone options with proper typing
type TimezoneOption = TypeaheadOption<string>;

interface UseJobTimezoneHierarchyProps {
  rawJob?: {
    id?: number;
    timeZone?: string | null;
  } | null;
  preventUserTimezoneFallback?: boolean;
  // New props for hierarchy support
  hierarchyContext?: TimezoneHierarchyContext;
  useHierarchy?: boolean;
}

// Utility function to detect user's timezone and map it to backend format
const detectUserTimezone = (): string | null => {
  try {
    // Get the user's timezone from the browser
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!userTimezone) {
      logger.warn('Unable to detect user timezone from browser');
      return null;
    }

    return userTimezone;
  } catch (error) {
    logger.error('Error detecting user timezone', error);
    return null;
  }
};

// Helper function to find timezone by offset match
const findTimezoneByOffset = (
  userOffsetHours: number,
  availableTimezones: TimezoneOption[],
  userTimezone: string
): TimezoneOption | null => {
  const matchingTimezones = availableTimezones.filter((tz) => {
    const tzData = tz.data as unknown as TimezoneData;
    if (!tzData?.timeZone) return false;

    // More flexible regex pattern to handle single-digit hours (e.g., UTC+5:30)
    const utcMatch = tzData.timeZone?.match(/\(UTC([+-]\d{1,2}):\d{2}\)/);
    if (!utcMatch || !utcMatch[1]) return false;

    const tzOffsetHours = parseInt(utcMatch[1]);
    // Allow for 1.5 hour tolerance to handle 30-minute offsets and edge cases
    return Math.abs(tzOffsetHours - userOffsetHours) <= 1.5;
  });

  if (matchingTimezones.length === 0) return null;

  // Try to find one with a similar name first
  const nameMatch = matchingTimezones.find((tz) => {
    const tzData = tz.data as unknown as TimezoneData;
    // More robust name matching: extract city name and check for inclusion
    const userCity = userTimezone.split('/').pop()?.toLowerCase() || '';
    const tzName = tzData?.timeZone?.toLowerCase() || '';
    return userCity && tzName.includes(userCity);
  });

  return nameMatch || matchingTimezones[0] || null;
};

// Function to find the best matching timezone from the backend
const findBestMatchingTimezone = (userTimezone: string, availableTimezones: TimezoneOption[]): TimezoneOption | null => {
  // Try exact match from mapping
  const mappedTimezoneId = (timezoneMapping as Record<string, string>)[userTimezone];

  if (mappedTimezoneId) {
    const exactMatch = availableTimezones.find((tz) => tz.value === mappedTimezoneId);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Try to find by name matching (for cases where mapping doesn't work)
  const userCity = userTimezone.split('/').pop()?.toLowerCase() || '';

  if (userCity) {
    const nameMatch = availableTimezones.find((tz) => {
      const tzData = tz.data as unknown as TimezoneData;
      const tzName = tzData?.timeZone?.toLowerCase() || '';
      const tzLabel = tz.label.toLowerCase();
      const tzValue = tz.value.toLowerCase();

      return tzName.includes(userCity) || tzLabel.includes(userCity) || tzValue.includes(userCity);
    });

    if (nameMatch) {
      return nameMatch;
    }
  }

  // Try to find by offset matching as a last resort
  try {
    const now = new Date();
    const userOffsetHours = -now.getTimezoneOffset() / 60;
    return findTimezoneByOffset(userOffsetHours, availableTimezones, userTimezone);
  } catch (error) {
    logger.warn('Error calculating timezone offset', error);
  }

  return null;
};

// Function to determine the best timezone based on hierarchy
// Hierarchy order:
// 1. Job Timezone (if exists) - Not applicable for New Jobs
// 2. Jobsite coordinates (Google Maps API) - handled by useJobSiteTimeZone, value passed via userOverride when populated
// 3. Organization default (api/v3/global-settings/default-timezone saved in localStorage)
// 4. User override (if set)
// 5. Browser timezone (Intl API)
const determineBestTimezoneFromHierarchy = (
  hierarchyContext: TimezoneHierarchyContext,
  availableTimezones: TimezoneOption[],
  isNewJob: boolean
): TimezoneOption | null => {
  // 1. Job Timezone (highest priority) - Not applicable for New Jobs
  if (!isNewJob && hierarchyContext.jobTimezone) {
    const jobTimezone = availableTimezones.find((tz) => tz.value === hierarchyContext.jobTimezone);
    if (jobTimezone) {
      logger.debug('Using job timezone from hierarchy:', jobTimezone.value);
      return jobTimezone;
    }
  }

  // 2. Jobsite coordinates (Google Maps API)
  // Note: useJobSiteTimeZone handles this and populates values.timeZone directly
  // If jobsite coordinates exist and userOverride is set, it means jobsite timezone was populated
  // We check this by verifying if there are jobsite coordinates AND userOverride matches
  if (hierarchyContext.jobsiteCoordinates?.latitude && hierarchyContext.jobsiteCoordinates?.longitude && hierarchyContext.userOverride) {
    const jobsiteTimezone = availableTimezones.find((tz) => tz.value === hierarchyContext.userOverride);
    if (jobsiteTimezone) {
      logger.debug('Using jobsite timezone from Google Maps API:', jobsiteTimezone.value);
      return jobsiteTimezone;
    }
  }

  // 3. Organization default (from localStorage)
  const storedTimezone = organizationTimezoneService.getOrganizationDefaultTimezone();
  if (storedTimezone) {
    const matchingTimezone = availableTimezones.find((tz) => tz.value === storedTimezone);
    if (matchingTimezone) {
      logger.debug('Using organization default timezone from localStorage:', matchingTimezone.value);
      return matchingTimezone;
    } else {
      // Try to find by partial name match as fallback
      const storedLower = storedTimezone.toLowerCase();
      const partialMatch = availableTimezones.find((tz) => {
        const tzLabel = tz.label.toLowerCase();
        const tzValue = tz.value.toLowerCase();
        // Check if stored timezone name appears in label or value, or vice versa
        return (
          tzLabel.includes(storedLower) || storedLower.includes(tzLabel) || tzValue.includes(storedLower) || storedLower.includes(tzValue)
        );
      });
      if (partialMatch) {
        logger.debug('Using organization default timezone (partial match) from localStorage:', partialMatch.value);
        return partialMatch;
      }
    }
  }

  // 4. User override (if explicitly set and not from jobsite)
  // Only use if it's not already handled by jobsite coordinates check above
  if (hierarchyContext.userOverride) {
    // Skip if we already checked this as jobsite timezone
    const isJobsiteTimezone =
      hierarchyContext.jobsiteCoordinates?.latitude &&
      hierarchyContext.jobsiteCoordinates?.longitude &&
      availableTimezones.some((tz) => tz.value === hierarchyContext.userOverride);

    if (!isJobsiteTimezone) {
      const userTimezone = availableTimezones.find((tz) => tz.value === hierarchyContext.userOverride);
      if (userTimezone) {
        logger.debug('Using user override timezone from hierarchy:', userTimezone.value);
        return userTimezone;
      }
    }
  }

  // 5. Browser timezone detection (fallback)
  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserTimezone = findBestMatchingTimezone(userTimezone, availableTimezones);
    if (browserTimezone) {
      logger.debug('Using browser timezone from hierarchy:', browserTimezone.value);
      return browserTimezone;
    }
  } catch (error) {
    logger.warn('Failed to detect browser timezone:', error);
  }

  logger.debug('No suitable timezone found in hierarchy');
  return null;
};

export const useJobTimezoneHierarchy = ({
  rawJob,
  preventUserTimezoneFallback = false,
  hierarchyContext,
  useHierarchy = false
}: UseJobTimezoneHierarchyProps) => {
  const { values, setFieldValue } = useFormikContext<JobFormData>();
  const [initialTimeZone, setInitialTimeZone] = useState<TimezoneOption | null>(null);
  const [timezoneLoaded, setTimezoneLoaded] = useState(false);
  const [userHasSelectedTimezone, setUserHasSelectedTimezone] = useState(false);
  const [lastSearchResults, setLastSearchResults] = useState<TimezoneOption[]>([]);
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
  const userTimezoneDetectionAttempted = useRef(false);

  // Reset state when rawJob changes (different job loaded)
  useEffect(() => {
    // Always reset state when rawJob changes to ensure clean state for new job
    setInitialTimeZone(null);
    setTimezoneLoaded(false);
    setUserHasSelectedTimezone(false);
    setLastSearchResults([]);
    setLastSearchTerm('');
    userTimezoneDetectionAttempted.current = false;
  }, [rawJob?.id]);

  const {
    fetchTimeZoneById,
    fetchTimeZonesBySearch,
    initialTimeZones,
    error: timezoneError,
    isLoading: timezoneLoading,
    clearError: clearTimezoneError
  } = useTimeZoneTypeaheadFetch();

  // Memoize user timezone detection to avoid repeated calculations
  // Only detect user timezone if not prevented by jobsite selection
  const userLocalTimezone = useMemo(() => {
    if (preventUserTimezoneFallback) {
      return null;
    }
    const timezone = detectUserTimezone();
    return timezone ? findBestMatchingTimezone(timezone, initialTimeZones) : null;
  }, [initialTimeZones, preventUserTimezoneFallback]);

  // Check if this is a new job (no ID) or an existing job (has ID)
  const isNewJob = !rawJob?.id;

  // Determine the best timezone based on hierarchy when enabled
  // Follows complete hierarchy: Job Timezone > Jobsite coordinates > Organization default > User override > Browser timezone
  const hierarchyTimezone = useMemo(() => {
    if (!useHierarchy || !hierarchyContext || initialTimeZones.length === 0) {
      return null;
    }

    // For new jobs, skip Job Timezone (not applicable)
    // For existing jobs, include Job Timezone in hierarchy
    return determineBestTimezoneFromHierarchy(hierarchyContext, initialTimeZones, isNewJob);
  }, [
    useHierarchy,
    hierarchyContext?.jobTimezone,
    hierarchyContext?.jobsiteCoordinates?.latitude,
    hierarchyContext?.jobsiteCoordinates?.longitude,
    hierarchyContext?.userOverride,
    initialTimeZones.length,
    isNewJob
  ]);

  // Helper function to check if we should return cached results
  const shouldReturnCachedResults = (searchTerm: string, lastSearchResults: TimezoneOption[], lastSearchTerm: string): boolean => {
    return !searchTerm?.trim() && lastSearchResults.length > 0 && lastSearchTerm.trim().length > 0;
  };

  const fetchTimeZonesWithInitial = useCallback(
    async (searchTerm: string): Promise<TimezoneOption[]> => {
      // If no search term or empty search, return initial timezones with user timezone logic
      if (!searchTerm || searchTerm.trim().length === 0) {
        if (shouldReturnCachedResults(searchTerm, lastSearchResults, lastSearchTerm)) {
          return lastSearchResults;
        }

        // If initialTimeZones is not loaded yet, fetch some initial data
        if (initialTimeZones.length === 0) {
          const results = await fetchTimeZonesBySearch('America');
          return results.slice(0, 10); // Limit initial results
        }

        // Inline user timezone logic to avoid function dependencies
        if (!userHasSelectedTimezone && userLocalTimezone !== null) {
          const userTimezoneExists = initialTimeZones.some((tz) => tz.value === userLocalTimezone.value);
          if (!userTimezoneExists) {
            return [userLocalTimezone, ...initialTimeZones];
          }
        }
        return initialTimeZones;
      }

      const searchResults = await fetchTimeZonesBySearch(searchTerm);

      // Store the search results and term for future use
      setLastSearchResults(searchResults);
      setLastSearchTerm(searchTerm);

      return searchResults;
    },
    [fetchTimeZonesBySearch, initialTimeZones, userHasSelectedTimezone, lastSearchResults, lastSearchTerm, userLocalTimezone]
  );

  // Get the effective timezone from either rawJob or Formik values
  // For initial load, prioritize rawJob?.timeZone for existing jobs
  // When values.timeZone is set programmatically (e.g., from jobsite), the effect at line 437-465
  // will update initialTimeZone and mark it as user-selected to prevent override
  const effectiveTimezone = rawJob?.timeZone !== undefined ? rawJob?.timeZone : values.timeZone;

  // Helper function to determine if timezone should be loaded
  const shouldLoadTimezone = (
    effectiveTimezone: string | null | undefined,
    userHasSelectedTimezone: boolean,
    isNewJob: boolean,
    initialTimeZone: TimezoneOption | null,
    valuesTimeZone: string | null | undefined,
    rawJobTimeZone: string | null | undefined
  ): boolean => {
    if (!effectiveTimezone || effectiveTimezone === '') return false;

    // IMPORTANT: If values.timeZone is set and different from rawJob?.timeZone,
    // don't load rawJob?.timeZone as it would override the jobsite timezone
    if (!isNewJob && valuesTimeZone && valuesTimeZone !== rawJobTimeZone) {
      // Form value is set and different from rawJob - don't load rawJob timezone
      // The effect at line 437-465 will handle updating initialTimeZone from values.timeZone
      return false;
    }

    // For existing jobs, load the timezone from the job if it exists
    // This ensures initialTimeZone is populated for initial display
    if (!isNewJob) {
      // Only load if initialTimeZone doesn't match effectiveTimezone
      return !initialTimeZone || initialTimeZone.value !== effectiveTimezone;
    }

    // For new jobs, only load if no timezone is already set and user hasn't selected one
    return initialTimeZone === null && !userHasSelectedTimezone;
  };

  // Load initial timezone when rawJob or Formik values change
  // Also trigger when initialTimeZones are loaded to handle cases where rawJob loads first
  useEffect(() => {
    const loadInitialTimeZone = async () => {
      const shouldLoad = shouldLoadTimezone(
        effectiveTimezone,
        userHasSelectedTimezone,
        isNewJob,
        initialTimeZone,
        values.timeZone,
        rawJob?.timeZone
      );

      if (shouldLoad && effectiveTimezone) {
        try {
          const timezone = await fetchTimeZoneById(effectiveTimezone);
          setInitialTimeZone(timezone);
          setTimezoneLoaded(true);
        } catch (error) {
          logger.error('Failed to fetch timezone by ID', error);
          setInitialTimeZone(null);
          setTimezoneLoaded(true);
        }
      } else if (!effectiveTimezone && timezoneLoaded) {
        setInitialTimeZone(null);
        setTimezoneLoaded(false);
      }
    };

    loadInitialTimeZone();
  }, [
    effectiveTimezone,
    fetchTimeZoneById,
    timezoneLoaded,
    userHasSelectedTimezone,
    rawJob?.id, // Use only ID instead of entire object to prevent unnecessary re-renders
    rawJob?.timeZone, // Also track timeZone changes
    initialTimeZones.length,
    initialTimeZone,
    isNewJob,
    values.timeZone // Track form value to prevent override
  ]);

  // Auto-determine timezone from hierarchy when enabled
  // Follows complete hierarchy: Job Timezone > Jobsite coordinates > Organization default > User override > Browser timezone
  // Note: If jobsite coordinates exist, useJobSiteTimeZone will handle timezone population first
  // This effect only applies hierarchy when there are no jobsite coordinates or jobsite timezone population failed
  useEffect(() => {
    // Check if user explicitly cleared the timezone - if so, don't re-apply
    const isExplicitlyCleared = localStorage.getItem('timezoneExplicitlyCleared') === 'true';
    if (isExplicitlyCleared) {
      logger.debug('Timezone was explicitly cleared by user, skipping hierarchy auto-selection');
      return;
    }

    // Don't apply hierarchy if jobsite coordinates exist - let useJobSiteTimeZone handle it first
    // Only apply hierarchy if there are no jobsite coordinates or if jobsite timezone population already happened
    const hasJobsiteCoordinates = hierarchyContext?.jobsiteCoordinates?.latitude && hierarchyContext?.jobsiteCoordinates?.longitude;
    const jobsiteTimezoneAlreadyPopulated = hasJobsiteCoordinates && values.timeZone;

    if (
      useHierarchy &&
      !initialTimeZone &&
      !userHasSelectedTimezone &&
      isNewJob &&
      initialTimeZones.length > 0 &&
      (!hasJobsiteCoordinates || jobsiteTimezoneAlreadyPopulated)
    ) {
      // Apply timezone from hierarchy (hierarchy already follows correct order)
      // If jobsite coordinates exist but timezone wasn't populated, hierarchy will try Organization default > User override > Browser timezone
      if (hierarchyTimezone) {
        setInitialTimeZone(hierarchyTimezone);
        setTimezoneLoaded(true);
        logger.debug('Auto-selected timezone from hierarchy for new job:', hierarchyTimezone.value);
      } else {
        // No timezone found in hierarchy, keep field empty
        setTimezoneLoaded(true);
        logger.debug('No timezone found in hierarchy for new job - field will remain empty');
      }
    }
  }, [
    useHierarchy,
    hierarchyTimezone,
    initialTimeZone,
    userHasSelectedTimezone,
    isNewJob,
    initialTimeZones,
    effectiveTimezone,
    hierarchyContext?.jobsiteCoordinates?.latitude,
    hierarchyContext?.jobsiteCoordinates?.longitude,
    values.timeZone
  ]);

  // Reset initialTimeZone when user clears selection to prevent automatic re-selection
  useEffect(() => {
    // Only reset if the user has previously selected a timezone and now cleared it
    if (!values.timeZone && userHasSelectedTimezone && initialTimeZone) {
      setInitialTimeZone(null);
    }
  }, [values.timeZone, userHasSelectedTimezone, initialTimeZone]);

  /**
   * Determines whether the timezone should be synchronized with Formik based on the current state.
   */
  const shouldSyncTimezone = (
    isNewJob: boolean,
    effectiveTimezone: string | null | undefined,
    valuesTimeZone: string | null | undefined,
    userHasSelectedTimezone: boolean,
    initialTimeZone: TimezoneOption | null
  ): boolean => {
    // Base conditions that must be met for any synchronization
    if (!initialTimeZone || valuesTimeZone || userHasSelectedTimezone) {
      return false;
    }

    // For existing jobs: Always sync if the field is empty and we have an initial timezone
    // This ensures existing jobs get their timezone properly loaded from the database
    if (!isNewJob) {
      return true;
    }

    // For new jobs: Only sync if there's no effective timezone
    // This prevents overriding user's explicit clearing of the field
    return !effectiveTimezone;
  };

  // Syncs the Formik field value with the loaded initialTimeZone
  // This ensures the field is updated both for new jobs and existing jobs
  // But only when no user-defined value is already set
  useEffect(() => {
    // Check if user explicitly cleared the timezone - if so, don't sync
    const isExplicitlyCleared = localStorage.getItem('timezoneExplicitlyCleared') === 'true';
    if (isExplicitlyCleared) {
      logger.debug('Timezone was explicitly cleared by user, skipping sync');
      return;
    }

    const shouldSync = shouldSyncTimezone(isNewJob, effectiveTimezone, values.timeZone, userHasSelectedTimezone, initialTimeZone);

    if (shouldSync) {
      setFieldValue('timeZone', initialTimeZone!.value);
    }
  }, [initialTimeZone, values.timeZone, setFieldValue, userHasSelectedTimezone, isNewJob, effectiveTimezone]);

  // Listen for changes to the timezone field value and update initialTimeZone accordingly
  // This handles cases where the timezone is set programmatically (e.g., from jobsite)
  // IMPORTANT: Always sync initialTimeZone when values.timeZone changes to prevent hierarchy from overriding
  useEffect(() => {
    if (values.timeZone) {
      // If timezone value changed and it's different from current initialTimeZone, update it
      // This ensures that when jobsite timezone is set, initialTimeZone matches it
      // and prevents the hierarchy hook from overriding with rawJob?.timeZone
      if (!initialTimeZone || initialTimeZone.value !== values.timeZone) {
        const fetchAndSetInitialTimezone = async () => {
          try {
            const timezoneOption = await fetchTimeZoneById(values.timeZone!);
            if (timezoneOption) {
              setInitialTimeZone(timezoneOption);
              // Mark as user-selected to prevent hierarchy from overriding
              setUserHasSelectedTimezone(true);
              logger.debug('Updated initialTimeZone from form value (e.g., jobsite timezone)', {
                timezoneId: timezoneOption.value,
                timezoneName: timezoneOption.label
              });
            }
          } catch (error) {
            logger.error('Failed to fetch timezone for display', { timezoneId: values.timeZone, error });
          }
        };

        fetchAndSetInitialTimezone();
      }
    } else if (!values.timeZone && initialTimeZone) {
      // Check if user explicitly cleared the timezone
      const isExplicitlyCleared = localStorage.getItem('timezoneExplicitlyCleared') === 'true';
      if (isExplicitlyCleared) {
        // User explicitly cleared - clear initialTimeZone but keep userHasSelectedTimezone true
        // to prevent hierarchy from re-applying
        setInitialTimeZone(null);
        // Don't reset userHasSelectedTimezone to false - this prevents hierarchy from re-applying
        logger.debug('Timezone explicitly cleared by user, preventing hierarchy re-application');
      } else {
        // Timezone was cleared programmatically (not by user) - allow normal reset
        setInitialTimeZone(null);
        setUserHasSelectedTimezone(false);
      }
    }
  }, [values.timeZone, initialTimeZone, fetchTimeZoneById]);

  // Function to handle when user manually selects a timezone
  const handleTimezoneSelection = useCallback(
    (selectedTimezone: TimezoneOption | null) => {
      if (selectedTimezone) {
        // Clear the explicit clear flag when user selects a new timezone
        localStorage.removeItem('timezoneExplicitlyCleared');

        // Check if the selected timezone is the user's local timezone
        const isUserLocalTimezone = userLocalTimezone?.value === selectedTimezone.value;

        // Only mark as user-selected if it's not their local timezone
        if (!isUserLocalTimezone) {
          setUserHasSelectedTimezone(true);
        }

        setFieldValue('timeZone', selectedTimezone.value);

        // Clear search results when user selects an item
        setLastSearchResults([]);
        setLastSearchTerm('');
      } else {
        // When user clears the selection:
        // 1. Set explicit clear flag to prevent hierarchy from re-applying
        // 2. Clear the field value
        // 3. Reset the initialTimeZone to prevent automatic re-selection
        // 4. Clear search results
        // 5. Keep userHasSelectedTimezone as true to prevent hierarchy from re-applying
        localStorage.setItem('timezoneExplicitlyCleared', 'true');
        setFieldValue('timeZone', null);
        setInitialTimeZone(null);
        // Keep userHasSelectedTimezone true to prevent hierarchy from re-applying
        // This is important - if we set it to false, the hierarchy will re-apply
        setLastSearchResults([]);
        setLastSearchTerm('');
      }
    },
    [setFieldValue, userLocalTimezone]
  );

  return {
    userTimezone: userLocalTimezone,
    initialTimeZone,
    timezoneError,
    timezoneLoading,
    clearTimezoneError,
    fetchTimeZonesWithInitial,
    handleTimezoneSelection,
    // New hierarchy-specific returns
    hierarchyTimezone,
    hierarchySource: hierarchyTimezone ? 'Hierarchy' : null,
    availableTimezones: initialTimeZones
  };
};
